import React, { useState, useEffect, useCallback, useRef } from "react";
import { Calendar, RefreshCw, Trash2, Edit, Save } from "lucide-react";
import "./Form.css";
import General from "./General";
import Ratio from './Ratio';
import CreateReport from "./CreateReport";
import { API_URL } from "./API";

const CT = ({ goBack, savedFormData = null, project }) => {
  const RESET_EVENT_NAME = 'ctReportDataCleared';
  const [showApp, setShowApp] = useState(false);
  const [showRatioTest, setShowRatioTest] = useState(false);


  const [selectedCore, setSelectedCore] = useState(() => {
    try {
      const savedData = localStorage.getItem("ctFormData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.selectedCore) {
          return parsed.selectedCore;
        }
      }
    } catch (e) {
     
    }
    return savedFormData?.selectedCore || 1;
  });

  const [selectedSecTerm, setSelectedSecTerm] = useState(() => {
    try {
      const savedData = localStorage.getItem("ctFormData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.selectedSecTerm) {
          return parsed.selectedSecTerm;
        }
      }
    } catch (e) {

    }
    return savedFormData?.selectedSecTerm || "1S1-1S2";
  });

  const [selectedFrequency, setSelectedFrequency] = useState(() => {
    try {
      const savedData = localStorage.getItem("ctFormData");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        if (parsed.selectedFrequency) {
          return parsed.selectedFrequency;
        }
      }
    } catch (e) {
   
    }
    return savedFormData?.selectedFrequency || "";
  });
  const [currentDate, setCurrentDate] = useState("");

  const fetchClientName = async (clientId) => {
    try {
      const response = await fetch(`${API_URL}/clients/get_client`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        mode: 'cors',
      });

      if (response.ok) {
        const result = await response.json();
        let clientsData = Array.isArray(result) ? result : result.data || result.clients || [];
        const client = clientsData.find(c => c.client_id === clientId || c.id === clientId);
        return client ? client.client_name : "Unknown Client";
      }
    } catch (error) {
      
    }
    return "Unknown Client";
  };

  const [formState, setFormState] = useState(() => {
    const savedData = localStorage.getItem('ctFormData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      const selectedCoreCount = parsedData.selectedCore || 1;

      return {
        ...parsedData,
        coreDetails: parsedData.coreDetails ?
          parsedData.coreDetails.slice(0, selectedCoreCount) :
          Array(selectedCoreCount).fill().map(() => ({
            ratio: "",
            class: "Select Class",
            vk: "",
            vkValue: "",
            imageAtVkHalf: "",
            rct: "",
            appliedVoltages: {},
            measuredCurrents: {}
          }))
      };
    }

    if (savedFormData) {
      const coreCount = savedFormData.selectedCore || 1;
      return {
        ...savedFormData,
        coreDetails: savedFormData.coreDetails ?
          savedFormData.coreDetails.slice(0, coreCount) :
          Array(coreCount).fill().map(() => ({
            ratio: "",
            class: "Select Class",
            vk: "",
            vkValue: "",
            imageAtVkHalf: "",
            rct: "",
            appliedVoltages: {},
            measuredCurrents: {}
          }))
      };
    }

    return {
      project: "",
      date: "",
      reportNo: "",
      description: "",
      location: "",
      customer: "",
      client: "",
      yom: "",
      il: "",
      make: "",
      serialNo: "",
      stc: "",
      insulationClass: "",
      coreDetails: Array(1).fill().map(() => ({
        ratio: "",
        class: "Select Class",
        vk: "",
        vkValue: "",
        imageAtVkHalf: "",
        rct: "",
        appliedVoltages: {},
        measuredCurrents: {}
      }))
    };
  });

  const [vkValues, setVkValues] = useState(() => {
    const savedData = localStorage.getItem('ctFormData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      const selectedCoreCount = parsedData.selectedCore || 1;
      return parsedData.vkValues ?
        parsedData.vkValues.slice(0, selectedCoreCount) :
        Array(selectedCoreCount).fill("");
    }

    if (savedFormData) {
      const coreCount = savedFormData.selectedCore || 1;
      return savedFormData.vkValues ?
        savedFormData.vkValues.slice(0, coreCount) :
        Array(coreCount).fill("");
    }

    return Array(1).fill("");
  });

  const [savedGeneralData, setSavedGeneralData] = useState(null);
  const [savedRatioData, setSavedRatioData] = useState(null);
  const isClearingRef = useRef(false);

  const [formErrors, setFormErrors] = useState({
    description: "",
    date: "",
    project: "",
    reportNo: "",
    location: "",
    customer: "",
    client: "",
    yom: "",
    il: "",
    make: "",
    serialNo: "",
    stc: "",
    insulationClass: "",
    frequency: "",
    coreDetails: []
  });

  const percentages = [10, 20, 30, 40, 50, 60, 70, 80, 90, 100, 110];

  useEffect(() => {
    const loadProjectData = async () => {
      if (project) {
        let clientName = project?.clientName || project?.client_name;

        if (!clientName && project?.client) {
          clientName = await fetchClientName(project.client);
        }

        setFormState(prev => ({
          ...prev,
          project: project?.name || '',
          client: clientName || '',

        }));
      }
    };

    loadProjectData();
  }, [project]);

  const validateInput = (field, value) => {
    switch (field) {
      case "description":
        return /^[a-zA-Z/\s ]*$/.test(value);
      case "project":
        return /^[a-zA-Z0-9,\s ]*$/.test(value);
      case "reportNo":
        return /^[a-zA-Z0-9,\s- ]*$/.test(value);
      case "location":
        return /^[a-zA-Z0-9,\s- ]*$/.test(value);
      case "customer":
        return /^[a-zA-Z\s ]*$/.test(value);
      case "client":
        return /^[a-zA-Z\s ]*$/.test(value);
      case "make":
        return /^[a-zA-Z0-9\s ]*$/.test(value);
      case "serialNo":
        return /^[0-9/,\s ]*$/.test(value);
      case "stc":
        return /^[a-zA-Z0-9/\s ]*$/.test(value);
      case "insulationClass":
        return /^[a-zA-Z\s ]*$/.test(value);
      default:
        return true;
    }
  };

  const saveToLocalStorage = () => {

    if (isClearingRef.current) {
      return;
    }

    const dataToSave = {
      ...formState,
      selectedCore,
      selectedSecTerm,
      selectedFrequency,
      vkValues: vkValues.slice(0, selectedCore),
      coreDetails: formState.coreDetails.slice(0, selectedCore)
    };

    localStorage.setItem('ctFormData', JSON.stringify(dataToSave));
  };

  useEffect(() => {
    saveToLocalStorage();
  }, [formState, selectedCore, selectedSecTerm, selectedFrequency, vkValues]);

  useEffect(() => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    setCurrentDate(formattedDate);

    setFormState(prev => ({
      ...prev,
      date: prev.date || formattedDate
    }));
  }, []);

  const handleInputChange = (field, value) => {
    if (field === "yom") {
      const numeric = value.replace(/[^0-9]/g, '');
      if (numeric.length <= 4) {
        const currentYear = new Date().getFullYear();
        const enteredYear = parseInt(numeric);

        setFormState(prev => ({
          ...prev,
          [field]: numeric
        }));

        if (numeric.length === 4 && enteredYear > currentYear) {
          setFormErrors(prev => ({
            ...prev,
            [field]: `Year cannot be greater than ${currentYear}`
          }));
        } else {
          setFormErrors(prev => ({
            ...prev,
            [field]: ""
          }));
        }
      }
    } else {
      if (["description", "project", "reportNo", "location", "customer", "client", "make", "serialNo", "stc", "insulationClass"].includes(field)) {
        if (!validateInput(field, value)) {
          return;
        }
      }

      setFormState(prev => ({
        ...prev,
        [field]: value
      }));

      setFormErrors(prev => ({
        ...prev,
        [field]: ""
      }));
    }
  };

  const handleNext = (e) => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    saveToLocalStorage();
    setShowApp(true);
  };

  const validateForm = () => {
    const errors = {
      project: "",
      date: "",
      reportNo: "",
      description: "",
      location: "",
      customer: "",
      client: "",
      yom: "",
      frequency: "",
      coreDetails: []
    };

    let hasError = false;

    if (formState.yom) {
      const currentYear = new Date().getFullYear();
      const enteredYear = parseInt(formState.yom);
      if (formState.yom.length === 4 && enteredYear > currentYear) {
        errors.yom = `Year cannot be greater than ${currentYear}`;
        hasError = true;
      }
    }

    for (let i = 0; i < selectedCore; i++) {
      const coreErrors = {};
      const core = formState.coreDetails[i];

      if (!core || !core.ratio) {
        coreErrors.ratio = "Ratio is required";
        hasError = true;
      }

      if (!core || core.class === "Select Class") {
        coreErrors.class = "Class is required";
        hasError = true;
      }

      if (vkValues[i] === "Yes") {
        if (!core || !core.vkValue) {
          coreErrors.vkValue = "VK Value is required";
          hasError = true;
        }

        if (!core || !core.imageAtVkHalf) {
          coreErrors.imageAtVkHalf = "Image@VK/2 is required";
          hasError = true;
        }

        if (!core || !core.rct) {
          coreErrors.rct = "RCT is required";
          hasError = true;
        }
      }

      errors.coreDetails[i] = coreErrors;
    }

    setFormErrors(errors);
    return !hasError;
  };

  const handleCoreDetailChange = (coreIndex, field, value) => {
    setFormState(prev => {
      const updatedCoreDetails = [...prev.coreDetails];

      if (!updatedCoreDetails[coreIndex]) {
        updatedCoreDetails[coreIndex] = {
          ratio: "",
          class: "Select Class",
          vk: "",
          vkValue: "",
          imageAtVkHalf: "",
          rct: "",
          appliedVoltages: {},
          measuredCurrents: {}
        };
      }

      updatedCoreDetails[coreIndex] = {
        ...updatedCoreDetails[coreIndex],
        [field]: value
      };

      return {
        ...prev,
        coreDetails: updatedCoreDetails
      };
    });

    setFormErrors(prev => {
      const updatedCoreErrors = [...prev.coreDetails];
      if (updatedCoreErrors[coreIndex]) {
        updatedCoreErrors[coreIndex] = {
          ...updatedCoreErrors[coreIndex],
          [field]: ""
        };
      }
      return {
        ...prev,
        coreDetails: updatedCoreErrors
      };
    });
  };

  const updateAppliedVoltages = (coreIndex, voltages) => {
    setFormState(prev => {
      const updatedCoreDetails = [...prev.coreDetails];

      if (!updatedCoreDetails[coreIndex]) {
        updatedCoreDetails[coreIndex] = {
          ratio: "",
          class: "Select Class",
          vk: "",
          vkValue: "",
          imageAtVkHalf: "",
          rct: "",
          appliedVoltages: {},
          measuredCurrents: {}
        };
      }

      updatedCoreDetails[coreIndex] = {
        ...updatedCoreDetails[coreIndex],
        appliedVoltages: voltages
      };

      return {
        ...prev,
        coreDetails: updatedCoreDetails
      };
    });
  };

  const handleMeasuredCurrentChange = (coreIndex, percentage, value) => {
    setFormState(prev => {
      const updatedCoreDetails = [...prev.coreDetails];

      if (!updatedCoreDetails[coreIndex]) {
        updatedCoreDetails[coreIndex] = {
          ratio: "",
          class: "Select Class",
          vk: "",
          vkValue: "",
          imageAtVkHalf: "",
          rct: "",
          appliedVoltages: {},
          measuredCurrents: {}
        };
      }

      const updatedCurrents = {
        ...updatedCoreDetails[coreIndex].measuredCurrents,
        [percentage]: value
      };

      updatedCoreDetails[coreIndex] = {
        ...updatedCoreDetails[coreIndex],
        measuredCurrents: updatedCurrents
      };

      return {
        ...prev,
        coreDetails: updatedCoreDetails
      };
    });
  };

  const handleBackFromGeneral = (generalData) => {
    setSavedGeneralData(generalData);

    if (generalData) {
      const currentData = JSON.parse(localStorage.getItem('ctFormData') || '{}');
      const updatedData = { ...currentData, generalData };
      localStorage.setItem('ctFormData', JSON.stringify(updatedData));
    }

    setShowApp(false);
  };

  const handleBackFromRatio = (ratioData) => {
    setSavedRatioData(ratioData);

    if (ratioData) {
      const currentData = JSON.parse(localStorage.getItem('ctFormData') || '{}');
      const updatedData = { ...currentData, ratioData };
      localStorage.setItem('ctFormData', JSON.stringify(updatedData));
    }

    setShowRatioTest(false);
  };

  const handleGoToGeneral = (ratioData) => {
    setSavedRatioData(ratioData);

    if (ratioData) {
      const currentData = JSON.parse(localStorage.getItem('ctFormData') || '{}');
      const updatedData = { ...currentData, ratioData };
      localStorage.setItem('ctFormData', JSON.stringify(updatedData));
    }

    setShowRatioTest(false);
    setShowApp(true);
  };

  const handleBackToCreateReport = () => {
    saveToLocalStorage();
    if (goBack) {
      goBack();
    }
  };

  const clearFormData = useCallback(() => {

    isClearingRef.current = true;


    localStorage.removeItem('ctFormData');


    const emptyFormState = {
      project: "",
      date: "",
      reportNo: "",
      description: "",
      location: "",
      customer: "",
      client: "",
      yom: "",
      il: "",
      make: "",
      serialNo: "",
      stc: "",
      insulationClass: "",
      coreDetails: Array(1).fill().map(() => ({
        ratio: "",
        class: "Select Class",
        vk: "",
        vkValue: "",
        imageAtVkHalf: "",
        rct: "",
        appliedVoltages: {},
        measuredCurrents: {}
      }))
    };

    setFormState(emptyFormState);
    setVkValues(Array(1).fill(""));
    setSelectedCore(1);
    setSelectedSecTerm("1S1-1S2");
    setSelectedFrequency("");
    setFormErrors({
      description: "",
      date: "",
      project: "",
      reportNo: "",
      location: "",
      customer: "",
      client: "",
      yom: "",
      il: "",
      make: "",
      serialNo: "",
      stc: "",
      insulationClass: "",
      frequency: "",
      coreDetails: []
    });


    const clearedData = {
      ...emptyFormState,
      selectedCore: 1,
      selectedSecTerm: "1S1-1S2",
      selectedFrequency: "",
      vkValues: [""],
      coreDetails: emptyFormState.coreDetails
    };
    localStorage.setItem('ctFormData', JSON.stringify(clearedData));


    setTimeout(() => {
      isClearingRef.current = false;
    }, 200);
  }, [currentDate]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const handleGlobalReset = () => {
      clearFormData();
      setSavedGeneralData(null);
      setSavedRatioData(null);
      setShowApp(false);
      setShowRatioTest(false);
    };

    const handleDataReset = () => {
      clearFormData();
    };

    window.addEventListener(RESET_EVENT_NAME, handleGlobalReset);
    window.addEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    return () => {
      window.removeEventListener(RESET_EVENT_NAME, handleGlobalReset);
      window.removeEventListener('ctReportDataClearedNoRedirect', handleDataReset);
    };
  }, [clearFormData]);



  if (showRatioTest) {
    return (
      <Ratio
        selectedCore={selectedCore}
        initialData={savedRatioData}
        goBackToPolarity={handleBackFromRatio}
        goToGeneral={handleGoToGeneral}
      />
    );
  }


if (showApp) {
  return (
    <General
      selectedCore={selectedCore}
      goBack={handleBackFromGeneral}
      savedData={savedGeneralData}
      formData={formState}
    />
  );
}




  const secTermOptions = {
    1: ["1S1-1S2"],
    2: ["1S1-1S2", "2S1-2S2"],
    3: ["1S1-1S2", "2S1-2S2", "3S1-3S2"],
    4: ["1S1-1S2", "2S1-2S2", "3S1-3S2", "4S1-4S2"],
    5: ["1S1-1S2", "2S1-2S2", "3S1-3S2", "4S1-4S2", "5S1-5S2"]
  };

  const handleCoreChange = (e) => {
    const coreNumber = Number(e.target.value);
    const previousCoreCount = selectedCore;

    setSelectedCore(coreNumber);
    setSelectedSecTerm(secTermOptions[coreNumber][0]);

    setFormState(prev => {
      const newCoreDetails = [...prev.coreDetails];

      if (coreNumber > previousCoreCount) {
        for (let i = previousCoreCount; i < coreNumber; i++) {
          newCoreDetails[i] = {
            ratio: "",
            class: "Select Class",
            vk: "",
            vkValue: "",
            imageAtVkHalf: "",
            rct: "",
            appliedVoltages: {},
            measuredCurrents: {}
          };
        }
      } else if (coreNumber < previousCoreCount) {
        newCoreDetails.splice(coreNumber);
      }

      return {
        ...prev,
        coreDetails: newCoreDetails
      };
    });

    setVkValues(prev => {
      const newVkValues = [...prev];

      if (coreNumber > previousCoreCount) {
        for (let i = previousCoreCount; i < coreNumber; i++) {
          newVkValues[i] = "";
        }
      } else if (coreNumber < previousCoreCount) {
        newVkValues.splice(coreNumber);
      }

      return newVkValues;
    });

    localStorage.setItem('selectedRatioCores', coreNumber.toString());
  };

  const handleFrequencyChange = (e) => {
    setSelectedFrequency(e.target.value);

    setFormErrors(prev => ({
      ...prev,
      frequency: ""
    }));
  };

  const handleVkChange = (coreIndex, value) => {
    const newVkValues = [...vkValues];
    newVkValues[coreIndex] = value;
    setVkValues(newVkValues);

    handleCoreDetailChange(coreIndex, 'vk', value);

    if (value === "No") {
      handleCoreDetailChange(coreIndex, 'vkValue', "");
      handleCoreDetailChange(coreIndex, 'imageAtVkHalf', "");
      handleCoreDetailChange(coreIndex, 'rct', "");
    }
  };

  const handleVkValueChange = (coreIndex, value) => {
    handleCoreDetailChange(coreIndex, 'vkValue', value);

    if (value && vkValues[coreIndex] === "Yes") {
      const vkValue = parseFloat(value);
      if (!isNaN(vkValue)) {
        const calculatedVoltages = {};
        percentages.forEach(percentage => {
          let calculatedVoltage;
          if (percentage === 10) {
            calculatedVoltage = ((vkValue / 2) * 0.1).toFixed(2);
          } else if (percentage === 20) {
            calculatedVoltage = ((vkValue / 2) * 0.2).toFixed(2);
          } else {
            const factor = 0.2 + ((percentage - 20) / 10) * 0.1;
            calculatedVoltage = ((vkValue / 2) * factor).toFixed(2);
          }
          calculatedVoltages[percentage] = calculatedVoltage;
        });

        updateAppliedVoltages(coreIndex, calculatedVoltages);
      }
    }
  };

  const redirectToHome = () => {
    if (window.confirm("Are you sure you want to leave this page? Any unsaved changes will be lost.")) {
      localStorage.setItem('openReportsOnLoad', 'true');
      window.location.href = "/";
    }
  };

  return (
    <section className="d11">
      <section className="form-container">
        <span
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            fontSize: '20px',
            fontWeight: '600',
            color: '#333',
            cursor: 'pointer',
            marginLeft: '50px',
            marginBottom: '50px'
          }}
          onClick={redirectToHome}
        >
          X
        </span>

        <h2 className="r2">CURRENT TRANSFORMER INSPECTION / TEST REPORT</h2>

        <form>
          <div className="card">
            <div className="form-row second-row">
              <div className="form-group1">
                <label>Description</label>
                <input
                  type="text"
                  name="description"
                  placeholder="Enter Description"
                  value="CURRENT TRANSFORMER INSPECTION / TEST REPORT"
                  onChange={(e) => handleInputChange("description", e.target.value)}
                  readOnly
                  style={{ cursor: 'not-allowed' }}
                />
                {formErrors.description && <span className="error-message">{formErrors.description}</span>}
              </div>

              <div className="form-group1">
                <label>Project</label>
                <input
                  type="text"
                  name="project"
                  placeholder="Enter Project"
                  value={formState.project || ""}
                  onChange={(e) => handleInputChange("project", e.target.value)}
                  readOnly
                  style={{ cursor: 'not-allowed' }}
                />
                {formErrors.project && <span className="error-message">{formErrors.project}</span>}
              </div>

              <div className="form-group1">
                <label>Date</label>
                <input
                  type="date"
                  name="date"
                  placeholder="Select Date"
                  value={formState.date || ""}
                  onChange={(e) => handleInputChange("date", e.target.value)}
                />
                {formErrors.date && <span className="error-message">{formErrors.date}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group1">
                <label>Report No</label>
                <input
                  type="text"
                  name="reportNo"
                  placeholder="Enter Report No"
                  value={formState.reportNo || ""}
                  onChange={(e) => handleInputChange("reportNo", e.target.value)}
                />
                {formErrors.reportNo && <span className="error-message">{formErrors.reportNo}</span>}
              </div>

              <div className="form-group1">
                <label>Location</label>
                <input
                  type="text"
                  name="location"
                  placeholder="Enter Location"
                  value={formState.location || ""}
                  onChange={(e) => handleInputChange("location", e.target.value)}

                />
                {formErrors.location && <span className="error-message">{formErrors.location}</span>}
              </div>

              <div className="form-group1">
                <label>Customer</label>
                <input
                  type="text"
                  name="customer"
                  placeholder="Enter Customer"
                  value={formState.customer || ""}
                  onChange={(e) => handleInputChange("customer", e.target.value)}
                />
                {formErrors.customer && <span className="error-message">{formErrors.customer}</span>}
              </div>

              <div className="form-group1">
                <label>Client</label>
                <input
                  type="text"
                  name="client"
                  placeholder="Enter Client"
                  value={formState.client || ""}
                  onChange={(e) => handleInputChange("client", e.target.value)}
                  readOnly
                  style={{ cursor: 'not-allowed' }}
                />
                {formErrors.client && <span className="error-message">{formErrors.client}</span>}
              </div>
            </div>
          </div>

          <div className="card horizontal-cards">
            <div className="card-half1">
              <h3 className="place">NAME PLATE DETAILS</h3>
              <div className="form-row yom-il-stc">
                <div className="form-group2">
                  <label>YOM (Year of Manufacturing)</label>
                  <input
                    type="text"
                    name="yom"
                    placeholder="Enter Year of Manufacture"
                    value={formState.yom || ""}
                    onChange={(e) => handleInputChange("yom", e.target.value)}
                    maxLength={4}
                  />
                  {formErrors.yom && <span className="error-message">{formErrors.yom}</span>}
                </div>

                <div className="form-group2">
                  <label>IL (Insulation Level)</label>
                  <input
                    type="text"
                    name="il"
                    placeholder="Enter IL"
                    value={formState.il || ""}
                    onChange={(e) => handleInputChange("il", e.target.value)}
                  />
                </div>

                <div className="form-group1">
                  <label>Make</label>
                  <input
                    type="text"
                    name="make"
                    placeholder="Enter Make"
                    value={formState.make || ""}
                    onChange={(e) => handleInputChange("make", e.target.value)}
                  />
                  {formErrors.make && <span className="error-message">{formErrors.make}</span>}
                </div>

                <div className="form-group1">
                  <label>Serial No for R-Y-B</label>
                  <input
                    type="text"
                    name="serialNo"
                    placeholder="Enter Serial No"
                    value={formState.serialNo || ""}
                    onChange={(e) => handleInputChange("serialNo", e.target.value)}
                  />
                  {formErrors.serialNo && <span className="error-message">{formErrors.serialNo}</span>}
                </div>

                <div className="form-group2">
                  <label>STC (Short Circuit Current)</label>
                  <input
                    type="text"
                    name="stc"
                    placeholder="Enter STC"
                    value={formState.stc || ""}
                    onChange={(e) => handleInputChange("stc", e.target.value)}
                  />
                </div>

                <div className="form-group2">
                  <label>Insulation Class</label>
                  <input
                    type="text"
                    name="insulationClass"
                    placeholder="Enter Insulation class"
                    value={formState.insulationClass || ""}
                    onChange={(e) => handleInputChange("insulationClass", e.target.value)}
                  />
                  {formErrors.insulationClass && <span className="error-message">{formErrors.insulationClass}</span>}
                </div>
              </div>
            </div>

            <div className="card-half2">
              <div className="form-row">
                <div className="form-group2">
                  <label>Frequency</label>
                  <select
                    value={selectedFrequency}
                    onChange={handleFrequencyChange}
                  >
                    <option value="">Select Frequency</option>
                    <option value="50Hz">50Hz</option>
                    <option value="60Hz">60Hz</option>
                  </select>
                  {formErrors.frequency && <span className="error-message">{formErrors.frequency}</span>}
                </div>

                <div className="form-group2">
                  <label>Core</label>
                  <select
                    value={selectedCore}
                    onChange={handleCoreChange}
                  >
                    {[1, 2, 3, 4, 5].map((num) => (
                      <option key={num} value={num}>Core-{num}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          </div>

          {[...Array(selectedCore)].map((_, index) => (
            <div key={index} className="card core-details-section">
              <h3 className="place">Core {index + 1} Details</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Ratio(A)</label>
                  <input
                    type="text"
                    name="ratio"
                    placeholder="Enter Ratio"
                    value={formState.coreDetails[index]?.ratio || ""}
                    onChange={(e) => handleCoreDetailChange(index, "ratio", e.target.value)}
                  />
                  {formErrors.coreDetails[index]?.ratio && (
                    <span className="error-message">{formErrors.coreDetails[index].ratio}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>Class</label>
                  <select
                    name="class"
                    value={formState.coreDetails[index]?.class || "Select Class"}
                    onChange={(e) => handleCoreDetailChange(index, "class", e.target.value)}
                  >
                    <option>Select Class</option>
                    <option>0.1</option>
                    <option>0.2</option>
                    <option>0.5</option>
                    <option>0.2S</option>
                    <option>0.5S</option>
                    <option>0.1S</option>
                    <option>0.3</option>
                    <option>5P10</option>
                    <option>5P20</option>
                    <option>10P10</option>
                    <option>PX</option>
                    <option>PS</option>
                    <option>PR</option>
                    <option>TPX</option>
                    <option>TPY</option>
                    <option>TPZ</option>
                  </select>
                  {formErrors.coreDetails[index]?.class && (
                    <span className="error-message">{formErrors.coreDetails[index].class}</span>
                  )}
                </div>

                <div className="form-group">
                  <label>VK</label>
                  <div className="radio-group">
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`vk-${index}`}
                        value="Yes"
                        checked={vkValues[index] === "Yes"}
                        onChange={() => handleVkChange(index, "Yes")}
                      />
                      Yes
                    </label>
                    <label className="radio-label">
                      <input
                        type="radio"
                        name={`vk-${index}`}
                        value="No"
                        checked={vkValues[index] === "No"}
                        onChange={() => handleVkChange(index, "No")}
                      />
                      No
                    </label>
                  </div>
                </div>
              </div>

              {vkValues[index] === "Yes" && (
                <div className="vk-details-container">
                  <div className="form-row">
                    <div className="form-group">
                      <label className="label10">Vk Voltage</label>
                      <input
                        type="number"
                        placeholder="Enter VK Value"
                        value={formState.coreDetails[index]?.vkValue || ""}
                        onChange={(e) => handleVkValueChange(index, e.target.value)}
                        className="vk-value-input"
                      />
                      {formErrors.coreDetails[index]?.vkValue && (
                        <span className="error-message">{formErrors.coreDetails[index].vkValue}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>Image@VK/2</label>
                      <input
                        type="text"
                        placeholder="Enter Image@VK/2"
                        value={formState.coreDetails[index]?.imageAtVkHalf || ""}
                        onChange={(e) => handleCoreDetailChange(index, "imageAtVkHalf", e.target.value)}
                        className="vk-half-input"
                      />
                      {formErrors.coreDetails[index]?.imageAtVkHalf && (
                        <span className="error-message">{formErrors.coreDetails[index].imageAtVkHalf}</span>
                      )}
                    </div>

                    <div className="form-group">
                      <label>RCT</label>
                      <input
                        type="text"
                        placeholder="Enter RCT"
                        value={formState.coreDetails[index]?.rct || ""}
                        onChange={(e) => handleCoreDetailChange(index, "rct", e.target.value)}
                        className="rct-input"
                      />
                      {formErrors.coreDetails[index]?.rct && (
                        <span className="error-message">{formErrors.coreDetails[index].rct}</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}

          <div className="card">
            <div className="form-row">
              <label>SEC. TERM</label>
              <div className="sec-term-buttons">
                {secTermOptions[selectedCore].map((term, index) => (
                  <button
                    key={index}
                    type="button"
                    className={`sec-term-button ${selectedSecTerm === term ? "active-term" : ""}`}
                    onClick={() => setSelectedSecTerm(term)}
                  >
                    {term}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="btn-container51">
            <button
              className="prev0"
              type="button"
              onClick={handleBackToCreateReport}
            >
              Back
            </button>
            <button
              className="nxt0"
              type="button"
              onClick={handleNext}
            >
              Next
            </button>
          </div>
        </form>
      </section>
    </section>
  );
};

export default CT;
